import { request } from '@umijs/max';

export interface Permission {
  id: string;
  code: string;
  name: string;
  description?: string;
  groupId: string;
  parentPermissionCode?: string;
  displayOrder: number;
  hasViewScope?: boolean;
}

export interface PermissionGroup {
  id: string;
  code: string;
  name: string;
  description?: string;
  displayOrder: number;
  icon?: string;
  permissions: Permission[];
}

export interface PermissionTreeNode {
  code: string;
  name: string;
  description?: string;
  type: 'module' | 'category' | 'permission';
  hasViewScope?: boolean;
  displayOrder?: number;
  children?: PermissionTreeNode[];
}

export interface HierarchicalPermission {
  id: string;
  code: string;
  name: string;
  description?: string;
  hasViewScope?: boolean;
  children?: HierarchicalPermission[];
}

export interface HierarchicalModule {
  code: string;
  name: string;
  permissions?: HierarchicalPermission[];
  submodules?: HierarchicalModule[];
}

export interface HierarchicalPermissionGroup {
  id: string;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  displayOrder: number;
  modules: HierarchicalModule[];
}

export interface UserPermission {
  code: string;
  viewScope?: 'all' | 'team' | 'self';
  sourceRoleIds?: string[];
}

export interface PermissionConfigPermission {
  id: string;
  code: string;
  name: string;
  description?: string;
  parent?: string;
  display_order?: number;
  has_view_scope?: boolean;
  [key: string]: any;
}

export interface PermissionConfigGroup {
  id: string;
  code: string;
  name: string;
  description?: string;
  display_order?: number;
  icon?: string;
  permissions: PermissionConfigPermission[];
  [key: string]: any;
}

export interface PermissionConfigData {
  groups: PermissionConfigGroup[];
  [key: string]: any;
}

export interface RolePermissionAssignment {
  roleId: string;
  roleName: string;
  permissions: Array<{
    permissionCode: string;
    viewScope: 'all' | 'team' | 'self';
  }>;
}

export interface UserPermissionsResponse {
  permissions: UserPermission[];
  userId: string;
  teamIds: string[];
  permissionConfig: PermissionConfigData | null;
  rolePermissions: RolePermissionAssignment | null;
  roleAssignments?: RolePermissionAssignment[];
  rolesVersion?: number;
  roles_version?: number;
}

const CURRENT_USER_PERMISSIONS_CACHE_TTL_MS = 5_000;
let currentUserPermissionsInFlight:
  | { token: string; request: Promise<UserPermissionsResponse> }
  | undefined;
let currentUserPermissionsCache:
  | { token: string; value: UserPermissionsResponse; expiresAt: number }
  | undefined;

const getAdminToken = (): string => {
  if (typeof localStorage === 'undefined') {
    return '';
  }
  return localStorage.getItem('token') ?? '';
};

// Get all permission groups with their permissions
export async function getPermissionGroups(): Promise<PermissionGroup[]> {
  return request<PermissionGroup[]>('/api/permissions/groups', {
    method: 'GET',
  });
}

// Get permissions for a specific group
export async function getPermissionsByGroup(groupId: string): Promise<Permission[]> {
  return request<Permission[]>(`/api/permissions/groups/${groupId}/permissions`, {
    method: 'GET',
  });
}

// Get all permissions
export async function getAllPermissions(params?: {
  groupId?: string;
  parentCode?: string;
}): Promise<Permission[]> {
  return request<Permission[]>('/api/permissions', {
    method: 'GET',
    params,
  });
}

// Get permission presets
export async function getPermissionPresets() {
  return request<
    {
      id: string;
      code: string;
      name: string;
      permissionCodes: string[];
      isActive: boolean;
    }[]
  >('/api/permissions/presets', {
    method: 'GET',
  });
}

// Get hierarchical permissions
export async function getHierarchicalPermissions(): Promise<HierarchicalPermissionGroup[]> {
  return request<HierarchicalPermissionGroup[]>('/api/permissions/hierarchical', {
    method: 'GET',
  });
}

// Get current user's permissions
export async function getCurrentUserPermissions(force = false): Promise<UserPermissionsResponse> {
  const token = getAdminToken();
  const now = Date.now();
  if (force) currentUserPermissionsCache = undefined;

  if (
    !force &&
    currentUserPermissionsCache?.token === token &&
    currentUserPermissionsCache.expiresAt > now
  ) {
    return currentUserPermissionsCache.value;
  }

  if (!force && currentUserPermissionsInFlight?.token === token) {
    return currentUserPermissionsInFlight.request;
  }

  const pendingRequest = request<UserPermissionsResponse>('/api/auth/permissions', {
    method: 'GET',
  });

  currentUserPermissionsInFlight = { token, request: pendingRequest };

  try {
    const value = await pendingRequest;
    if (getAdminToken() === token && currentUserPermissionsInFlight?.request === pendingRequest) {
      currentUserPermissionsCache = {
        token,
        value,
        expiresAt: Date.now() + CURRENT_USER_PERMISSIONS_CACHE_TTL_MS,
      };
    }
    return value;
  } finally {
    if (currentUserPermissionsInFlight?.request === pendingRequest) {
      currentUserPermissionsInFlight = undefined;
    }
  }
}

// Get permissions for a specific user
export async function getUserPermissions(userId: string): Promise<UserPermission[]> {
  const response = await request<UserPermissionsResponse>(`/api/users/${userId}/permissions`, {
    method: 'GET',
  });
  return response.permissions;
}

// Check if current user has a specific permission
export async function checkPermission(permissionCode: string): Promise<boolean> {
  const response = await request<{ hasPermission: boolean }>('/api/users/me/permissions/check', {
    method: 'GET',
    params: { code: permissionCode },
  });
  return response.hasPermission;
}

// Batch check multiple permissions
export async function checkMultiplePermissions(
  permissions: string[],
): Promise<Record<string, boolean>> {
  return request<Record<string, boolean>>('/api/auth/permissions/check', {
    method: 'POST',
    data: { permissions },
  });
}

// Get all permissions with scopes for current user
export async function getAllPermissionsWithScopes(): Promise<
  Array<{
    code: string;
    name: string;
    group: string;
    viewScope: 'all' | 'team' | 'self' | null;
    hasPermission: boolean;
  }>
> {
  return request('/api/auth/permissions/all-with-scopes', {
    method: 'GET',
  });
}

const PERMISSION_DISPLAY_PARENT_OVERRIDES: Record<string, string> = {
  'product:change_log_view': 'product',
};

const PERMISSION_DISPLAY_ORDER_OVERRIDES: Record<string, number> = {
  'price_change_management:view': 4.1,
  'product:change_log_view': 4.2,
  'expired_product_management:view': 4.3,
  'expired_product_management:unsalable_report_view': 4.31,
  'expired_product_management:unsalable_report_create': 4.32,
  'expired_product_management:unsalable_report_follow_up': 4.33,
};

const resolvePermissionDisplayParentCode = (permission: Permission, groupCode: string): string => {
  return (
    PERMISSION_DISPLAY_PARENT_OVERRIDES[permission.code] ??
    permission.parentPermissionCode ??
    groupCode
  );
};

const resolvePermissionDisplayOrder = (permission: Permission): number => {
  return PERMISSION_DISPLAY_ORDER_OVERRIDES[permission.code] ?? permission.displayOrder;
};

function getPermissionOrder(nameA: string, nameB: string): number {
  const orderMap: Record<string, number> = {
    // Primary actions
    檢視: 1,
    建立與編輯: 2,
    匯出: 3,
    刪除: 4,

    // Sub-permissions under 檢視
    檢視成本: 11,

    // Sub-permissions under 建立與編輯
    編輯成本: 21,
    編輯價格: 22,

    // Module sub-sections (categories)
    產品集合: 30,
    折扣: 31,
    產品改價管理: 32,
    產品變更紀錄檢視: 33,

    // Procurement sub-modules
    採購機會: 40,
    採購報價: 41,
    採購訂單: 42,

    // Supplier sub-modules
    供應商: 50,
    供應商群組管理: 51,
    供應商訊息中心: 52,
    供應商跟進: 53,

    // Store sub-modules
    門店: 60,
    貨架: 61,
    PDA帳號: 62,

    // Inventory sub-modules
    庫存總覽: 70,
    庫存異常看板: 71,
    庫存變更紀錄: 72,
    盤點: 73,

    // Script sub-modules
    腳本: 80,
    腳本執行記錄: 81,

    // Data anomaly sub-modules
    重複條碼: 90,

    // Sales analysis sub-modules
    數據面板: 100,

    // POS sub-modules
    門店帳號: 110,
    支付方式: 111,
    POS應用版本: 112,
    POS會話: 113,
    網店管理: 114,

    // Market info sub-modules
    採集任務: 120,
    MI產品: 121,
    數據審核: 122,
    數據分析: 123,
    MI用戶: 124,

    // Warehouse transfer sub-modules
    調撥單: 130,
    門店退倉管理: 131,

    // User management sub-modules
    用戶: 140,
    團隊: 141,
    角色: 142,
    用户操作紀錄: 143,

    // Special permissions with scope
    '檢視（所有/自己團隊/自己）': 1,
    '建立與編輯（所有/自己團隊/自己）': 2,
    '刪除（所有/自己團隊/自己）': 4,
  };

  const orderA = orderMap[nameA] || 1000;
  const orderB = orderMap[nameB] || 1000;

  return orderA - orderB;
}

function sortNodeChildren(node: PermissionTreeNode) {
  if (node.children && node.children.length > 0) {
    node.children.sort((a, b) => {
      const displayOrderDiff =
        (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER);
      if (displayOrderDiff !== 0) return displayOrderDiff;

      const order = getPermissionOrder(a.name, b.name);
      if (order !== 0) return order;
      return a.name.localeCompare(b.name);
    });

    node.children.forEach((child) => sortNodeChildren(child));
  }
}

// Transform flat permission structure to tree structure
export function buildPermissionTree(groups: PermissionGroup[]): PermissionTreeNode[] {
  const tree: PermissionTreeNode[] = [];

  // Build tree structure based on parent-child relationships
  groups.forEach((group) => {
    const moduleNode: PermissionTreeNode = {
      code: group.code,
      name: group.name,
      type: 'module',
      children: [],
    };

    // Create a map for quick lookup of permissions in this group
    const permissionMap = new Map<string, Permission>();
    group.permissions.forEach((perm) => {
      permissionMap.set(perm.code, perm);
    });

    // Build a map of permission nodes
    const nodeMap = new Map<string, PermissionTreeNode>();

    // First pass: identify which nodes have children (they are categories, not permissions)
    const hasChildren = new Set<string>();
    group.permissions.forEach((perm) => {
      const displayParentCode = resolvePermissionDisplayParentCode(perm, group.code);
      if (displayParentCode && displayParentCode !== group.code) {
        hasChildren.add(displayParentCode);
      }
    });

    // Second pass: create nodes with correct type
    group.permissions.forEach((perm) => {
      const isCategory = hasChildren.has(perm.code);
      nodeMap.set(perm.code, {
        code: perm.code,
        name: perm.name,
        type: isCategory ? 'category' : 'permission',
        hasViewScope: !isCategory ? perm.hasViewScope : undefined,
        displayOrder: resolvePermissionDisplayOrder(perm),
        children: [],
      });
    });

    // Connect parents and children. Top-level permissions use the group code as
    // their parent, so we only create parent/child relationships when the
    // parent is another permission node.
    group.permissions.forEach((perm) => {
      const displayParentCode = resolvePermissionDisplayParentCode(perm, group.code);
      if (displayParentCode && displayParentCode !== group.code) {
        const parentNode = nodeMap.get(displayParentCode);
        const childNode = nodeMap.get(perm.code);
        if (parentNode && childNode) {
          if (!parentNode.children) {
            parentNode.children = [];
          }
          parentNode.children.push(childNode);
        }
      }
    });

    // Add root permissions (those without parents or whose parent is the group code)
    group.permissions.forEach((perm) => {
      const displayParentCode = resolvePermissionDisplayParentCode(perm, group.code);
      if (!displayParentCode || displayParentCode === group.code) {
        const node = nodeMap.get(perm.code);
        if (node) {
          moduleNode.children!.push(node);
        }
      }
    });

    // Sort children by display order if available
    sortNodeChildren(moduleNode);

    tree.push(moduleNode);
  });

  return tree;
}
